import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../../../../components/2025/Header';
import Footer from '../../../../components/2025/Footer';
import HeroTitle from '../../../../components/2025/HeroTitle';
import { Chip } from '../../../../components/2025/Chip';
import LoadingSpinner from '../../../../components/2025/LoadingSpinner';
import { getNewsById } from '../../../../services/newsService';
import { news, blogType } from '../../../../types/cms-types';
import { optimizeImage } from '../../../../utils/imageOptimizer';
import styles from './styles.module.scss';

const NewsDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [newsData, setNewsData] = useState<news | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNewsDetail = async () => {
      if (!id) {
        navigate('/2025/news');
        return;
      }

      setLoading(true);
      const data = await getNewsById(id);

      if (!data) {
        navigate('/2025/news');
        return;
      }

      setNewsData(data);
      setLoading(false);
    };

    fetchNewsDetail();
  }, [id, navigate]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  };

  if (loading) {
    return (
      <div className={styles.newsDetailContainer}>
        <Header />
        <HeroTitle title="NEWS" subtitle="ニュース" />
        <div className={styles.newsDetail}>
          <div className={styles.container}>
            <LoadingSpinner />
          </div>
          <Footer />
        </div>
      </div>
    );
  }

  if (!newsData) {
    return null;
  }

  const categoryName = (newsData.post_category as blogType)?.category_types || 'お知らせ';

  return (
    <div className={styles.newsDetailContainer}>
      <Header />
      <HeroTitle title="NEWS" subtitle="ニュース" />
      <div className={styles.newsDetail}>
        <svg
          className={styles.decorationSvg}
          width="65"
          height="83"
          viewBox="0 0 65 83"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="15.5556" cy="25.2778" r="15.5556" fill="#F6B8DF" />
          <circle cx="41.5279" cy="49.3056" r="32.2222" stroke="#F6B8DF" strokeWidth="2.5" />
          <circle cx="41.5278" cy="6.52784" r="6.52778" fill="white" />
        </svg>

        <svg
          className={styles.decorationSvg2}
          width="81"
          height="86"
          viewBox="0 0 81 86"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="-58"
            y="72.6886"
            width="152.577"
            height="18.2606"
            rx="9.1303"
            transform="rotate(-29.7401 -58 72.6886)"
            fill="url(#paint0_linear_2467_1083)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_2467_1083"
              x1="-58"
              y1="81.8189"
              x2="94.5775"
              y2="81.8189"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#0946F1" />
              <stop offset="1" stopColor="#F1ECE6" />
            </linearGradient>
          </defs>
        </svg>

        <svg
          className={styles.decorationSvg3}
          width="50"
          height="86"
          viewBox="0 0 50 86"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="-89"
            y="72.6886"
            width="152.577"
            height="18.2606"
            rx="9.1303"
            transform="rotate(-29.7401 -89 72.6886)"
            fill="url(#paint0_linear_2467_1081)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_2467_1081"
              x1="-89"
              y1="81.8189"
              x2="63.5775"
              y2="81.8189"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#7B8DF2" />
              <stop offset="1" stopColor="#F6B8DF" />
            </linearGradient>
          </defs>
        </svg>

        <svg
          className={styles.decorationSvg4}
          width="120"
          height="141"
          viewBox="0 0 120 141"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="49.6515" cy="70.5972" r="69.7208" fill="#F6B8DF" />
        </svg>

        <svg
          className={styles.decorationSvg5}
          width="86"
          height="181"
          viewBox="0 0 86 181"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="-4.08789"
            y="166.771"
            width="299.576"
            height="22.2721"
            rx="11.1361"
            transform="rotate(-34.6137 -4.08789 166.771)"
            fill="url(#paint0_linear_2467_1087)"
            fillOpacity="0.5"
          />
          <defs>
            <linearGradient
              id="paint0_linear_2467_1087"
              x1="-4.08789"
              y1="177.907"
              x2="295.488"
              y2="177.907"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#4D83FE" />
              <stop offset="1" stopColor="#003078" />
            </linearGradient>
          </defs>
        </svg>

        <svg
          className={styles.decorationSvg6}
          width="41"
          height="41"
          viewBox="0 0 41 41"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M28.8162 0.995938C29.858 0.0372442 31.518 0.9718 31.2386 2.35977L28.8438 14.2587C28.7189 14.8795 29.0078 15.51 29.5596 15.8207L40.1361 21.7753C41.3698 22.4699 40.994 24.3374 39.5876 24.5006L27.531 25.8999C26.902 25.9729 26.3916 26.4426 26.2667 27.0633L23.8718 38.9623C23.5925 40.3503 21.7003 40.5699 21.1104 39.2828L16.0538 28.2487C15.7901 27.6731 15.1856 27.3328 14.5567 27.4058L2.50005 28.8052C1.09369 28.9684 0.300086 27.2366 1.34191 26.2779L10.2734 18.0591C10.7393 17.6304 10.8762 16.9504 10.6124 16.3748L5.55584 5.34065C4.96601 4.05356 6.36778 2.76365 7.60149 3.45823L18.178 9.41285C18.7298 9.72347 19.4188 9.6435 19.8847 9.21476L28.8162 0.995938Z"
            fill="#F6B8DF"
          />
        </svg>

        <svg
          className={styles.decorationSvg7}
          width="375"
          height="482"
          viewBox="0 0 375 482"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="-149.442"
            y="457.165"
            width="853.012"
            height="37.8276"
            rx="18.9138"
            transform="rotate(-32.9591 -149.442 457.165)"
            fill="url(#paint0_linear_2467_1075)"
            fillOpacity="0.5"
          />
          <defs>
            <linearGradient
              id="paint0_linear_2467_1075"
              x1="-149.442"
              y1="476.078"
              x2="703.569"
              y2="476.078"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#0007D2" />
              <stop offset="1" stopColor="white" />
            </linearGradient>
          </defs>
        </svg>

        <div className={styles.container}>
          <article className={styles.article}>
            <h1 className={styles.article__title}>{newsData.post_title}</h1>

            <div className={styles.article__meta}>
              <time className={styles.article__date}>{formatDate(newsData.publishedAt)}</time>
              {newsData.post_auther && (
                <p className={styles.article__author}>{newsData.post_auther}</p>
              )}
            </div>

            <div className={styles.article__chips}>
              <Chip text={categoryName} variant="secondary" />
              {newsData.post_tag &&
                newsData.post_tag.map(
                  (tag, index) =>
                    tag.post_tag && <Chip key={index} text={tag.post_tag} variant="secondary" />,
                )}
            </div>

            <div className={styles.article__divider} />

            {newsData.post_image && (
              <div className={styles.article__image}>
                <img
                  src={optimizeImage(newsData.post_image.url, { width: 1200, quality: 85 })}
                  alt={newsData.post_title}
                  width={newsData.post_image.width}
                  height={newsData.post_image.height}
                  loading="lazy"
                />
              </div>
            )}

            {newsData.post_detail && (
              <div
                className={styles.article__content}
                dangerouslySetInnerHTML={{ __html: newsData.post_detail }}
              />
            )}

            <div className={styles.article__footer}>
              <Link to="/2025/news" className={styles.article__backButton}>
                <svg
                  width="13"
                  height="8"
                  viewBox="0 0 13 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M12.4409 4.00781H0.970898" stroke="currentColor" strokeLinecap="round" />
                  <path
                    d="M4.67578 0.707031L0.558105 4.00115"
                    stroke="currentColor"
                    strokeLinecap="round"
                  />
                  <path
                    d="M4.67578 7.30193L0.558105 4.00781"
                    stroke="currentColor"
                    strokeLinecap="round"
                  />
                </svg>
                <span>ニュース一覧に戻る</span>
              </Link>
            </div>
          </article>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default NewsDetail;
